import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const devWarn = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};

export function AuthProvider({ children }) {
  // null = unknown, false = unauthenticated, object = admin session
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Auth lives in the httpOnly access_token cookie set by the backend on login.
  // The cookie is sent automatically because axios is configured with withCredentials: true.
  // Only check the session on /admin routes — public visitors never need it
  // (avoids a guaranteed 401 network call on every storefront visit).
  const { pathname } = useLocation();
  const checked = useRef(false);
  useEffect(() => {
    if (!pathname.startsWith("/admin")) {
      if (!checked.current) setLoading(false);
      return;
    }
    if (checked.current) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/auth/me");
        if (!cancelled) setAdmin(r.data);
      } catch (err) {
        // 401 for first-time visitors is the expected case — not an error worth logging.
        if (!cancelled) setAdmin(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
          // Only mark done on a real (non-cancelled) completion —
          // StrictMode's first mount/unmount cycle must not consume the check.
          checked.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setAdmin(r.data);
    return r.data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network errors during logout are non-fatal — we still clear local state.
      devWarn("[auth] logout request failed", err?.message || err);
    }
    setAdmin(false);
  };

  const value = useMemo(
    () => ({ admin, loading, login, logout }),
    [admin, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
